import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import type { CountryCode } from '@shared/constants/countries';

import CountryFlag from '@/shared/ui/country-flag';
import { countryName } from '@/utils/format';

import { countryOptions } from '../domain/licence-identity';
import { LicenceFieldLabel } from './licence-field';
import { licenceFieldSx } from './licence-field.styles';

export interface CountryFieldProps {
  id: string;
  value: CountryCode | '';
  onChange: (value: CountryCode | '') => void;
  error?: string;
  disabled?: boolean;
}

/**
 * "Based in": an Autocomplete over every ISO 3166-1 alpha-2 code, named in
 * English by `Intl.DisplayNames`: 249 entries are too many to scroll, so the
 * person types "fra" and picks France. The value is the code; only the label is
 * a name.
 */
export default function CountryField({ id, value, onChange, error, disabled }: CountryFieldProps) {
  return (
    <div>
      <LicenceFieldLabel htmlFor={id}>Based in</LicenceFieldLabel>
      <Autocomplete
        id={id}
        options={countryOptions()}
        value={value || null}
        onChange={(_, next) => onChange(next ?? '')}
        getOptionLabel={(code) => countryName(code)}
        disabled={disabled}
        autoHighlight
        renderOption={({ key, ...props }, code) => (
          <Box component="li" key={key} {...props} sx={{ display: 'flex', gap: 1.25 }}>
            <CountryFlag country={code} size={16} />
            {countryName(code)}
          </Box>
        )}
        renderInput={({ slotProps, ...params }) => (
          <TextField
            {...params}
            required
            hiddenLabel
            variant="filled"
            placeholder="Search a country"
            error={Boolean(error)}
            helperText={error}
            slotProps={{
              input: { ...slotProps.input, disableUnderline: true },
              htmlInput: slotProps.htmlInput,
            }}
            sx={(t) => ({
              ...licenceFieldSx(t),
              // Autocomplete pads its root AND its input; zero the root's vertical
              // padding so the input's own keeps the height of the other fields.
              '& .MuiFilledInput-root': {
                ...licenceFieldSx(t)['& .MuiFilledInput-root'],
                py: 0,
                pl: 1.5,
              },
              // MUI's own rule for a hidden-label filled Autocomplete is four classes
              // deep; `&&` outranks it.
              '&& .MuiFilledInput-root.MuiInputBase-hiddenLabel .MuiAutocomplete-input': { py: 1.5, px: 0 },
            })}
          />
        )}
      />
    </div>
  );
}
